/**
 * Reverse-lookup hook: given a managed File id, returns the relay
 * email/thread that the file was originally created from.
 *
 * Returns null (not an error) when the file was uploaded directly rather
 * than promoted from a relay attachment — the backend 404s in that case and
 * the API client normalises it to null.
 *
 * `staleTime: Infinity` because source provenance never changes after creation.
 */

import {useQuery} from '@tanstack/react-query';
import {useRequiredTenantId} from '@/matic/hooks/useRequiredTenantId';
import {getRelayApi} from '../api/relay-api';
import {relayKeys} from '../query-keys';

export function useAttachmentSourceForFile(
  fileId: string | undefined,
  enabled = true
) {
  const tenantId = useRequiredTenantId();
  return useQuery({
    queryKey: relayKeys.attachments.sourceByResource(fileId ?? '', tenantId),
    queryFn: async () => getRelayApi().getAttachmentSourceForResource(fileId!),
    enabled: Boolean(fileId) && enabled,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });
}
